import {isNumber} from 'lodash';
import React from 'react';
import type {WithRouterProps} from 'react-router';
import {withRouter} from 'react-router';

import {Button, Icon, LoadingSpinner, Text} from '@albert-io/atomic';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {calculateDueDate} from 'lib/timeUtils';
import {DueDateBadge} from './DueDateBadge';
import {ProficiencyChartBadges} from 'client/Reports/components/Table/Columns/ProficiencyChart/ProficiencyChart';
import type {DiagnosticItem, SkillSubjectGroup} from './types';
import TabEmptyState from '../TabEmptyState';

interface StudentSkillPathListProps {
  skillSubjectGroups: SkillSubjectGroup[];
  diagnostics: DiagnosticItem[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

interface SkillPathItemProps extends WithRouterProps<{}> {
  guideLevelId: string;
  name: string;
  code?: string | null;
  dueDate?: string | null;
  myProficiencyLevel?: number | null;
}

const SkillPathItemInner = ({
  router,
  guideLevelId,
  name,
  code,
  dueDate,
  myProficiencyLevel
}: SkillPathItemProps) => {
  // React Compiler opt-out: calculateDueDate() derives from current time (moment() +
  // systemTimeOffsetStore) in render, so compiling would memoize dueDateInfo as
  // dependency-free and freeze it for the session. Same class as Assignment.tsx.
  // A plain comment isn't enough here: other components in this file carry 'use
  // memo', which is enough for the compiler to compile this function too — the
  // explicit 'use no memo' below is required to actually exclude it.
  'use no memo';

  const handleClick = () => {
    router.push(`/adaptive/skill/${guideLevelId}`);
  };

  const dueDateInfo = dueDate ? calculateDueDate(dueDate) : null;

  return (
    <div
      className='flex cursor-pointer flex-wrap items-center justify-between gap-x0-half p-x0-half hover:background-subtle'
      role='button'
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleClick();
      }}
    >
      <div className='flex flex-wrap items-baseline gap-x1'>
        <Text as={MarkdownRendererV2} text={name} size='m' color='primary' />
        <Text size='m' color='tertiary'>
          ({code})
        </Text>
      </div>
      <div className='flex items-center gap-x1-half'>
        <div className='min-w-fit'>{dueDateInfo && <DueDateBadge dueDateInfo={dueDateInfo} />}</div>
        <ProficiencyChartBadges
          proficiencyLevel={myProficiencyLevel}
          showNotStarted={false}
          showLabel={false}
          badgeSize={16}
          iconGap={0}
          className='proficiency-chart__student_browsing [&_.empty-badge-component]:fill-background-bold!'
          withTooltip={isNumber(myProficiencyLevel)}
        />
      </div>
    </div>
  );
};

const SkillPathItem = withRouter(SkillPathItemInner);

interface DiagnosticPathItemProps extends WithRouterProps<{}> {
  diagnostic: DiagnosticItem;
}

const DiagnosticPathItemInner = ({router, diagnostic}: DiagnosticPathItemProps) => {
  // React Compiler opt-out: calculateDueDate() derives from current time (moment() +
  // systemTimeOffsetStore) in render, so compiling would memoize dueDateInfo as
  // dependency-free and freeze it for the session. Same class as Assignment.tsx.
  // A plain comment isn't enough here: other components in this file carry 'use
  // memo', which is enough for the compiler to compile this function too — the
  // explicit 'use no memo' below is required to actually exclude it.
  'use no memo';

  const handleClick = () => {
    router.push(`/diagnostics/${diagnostic.diagnosticId}`);
  };

  const dueDateInfo = diagnostic.dueDate ? calculateDueDate(diagnostic.dueDate) : null;

  return (
    <div
      className='flex cursor-pointer flex-wrap items-center justify-between gap-x2 rounded-x1 p-x1 hover:background-subtle'
      role='button'
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className='flex items-center gap-x2'>
        <div className='flex h-x5 w-x5 shrink-0 items-center justify-center rounded-full background-info-subtle'>
          <Icon icon={['fal', 'file-contract']} className='content-info!' />
        </div>
        <div className='flex flex-col'>
          <Text size='m' color='primary'>
            {diagnostic.name}
          </Text>
          <Text size='s' color='tertiary'>
            {`${diagnostic.guideName} • ${diagnostic.subjectName}`}
          </Text>
        </div>
      </div>
      <div className='min-w-fit'>{dueDateInfo && <DueDateBadge dueDateInfo={dueDateInfo} />}</div>
    </div>
  );
};

const DiagnosticPathItem = withRouter(DiagnosticPathItemInner);

interface BrowseAllSkillsLinkProps extends WithRouterProps<{}> {
  href: string;
}

const BrowseAllSkillsLinkInner = ({router, href}: BrowseAllSkillsLinkProps) => {
  'use memo';

  return (
    <Button
      color='secondary'
      variant='outlined'
      size='s'
      onClick={() => router.push(href)}
      roundness='pill'
      className='w-[149px]'
    >
      <Text size='m' color='inherit' className='font-normal'>
        Browse all skills
      </Text>
      <Icon className='ml-x1-quarter' icon={['far', 'arrow-up-right']} />
    </Button>
  );
};

const BrowseAllSkillsLink = withRouter(BrowseAllSkillsLinkInner);

export function SkillPathList({
  skillSubjectGroups,
  diagnostics,
  isLoading,
  isError,
  onRetry
}: StudentSkillPathListProps) {
  'use memo';

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-x4'>
        <LoadingSpinner size={3} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex flex-col gap-x4 border-t border-t-border-subtle p-x2 m:p-x4'>
        <TabEmptyState
          copy="We couldn't load your skill path."
          subtitle='Something went wrong. Check your connection and try again.'
          icon={['fal', 'circle-exclamation']}
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (skillSubjectGroups.length === 0 && diagnostics.length === 0) {
    return (
      <div className='flex flex-col gap-x4 border-t border-t-border-subtle p-x2 m:p-x4'>
        <TabEmptyState
          copy='Adaptive subjects and skills will appear here if you have access.'
          icon={['fal', 'sparkles']}
        />
      </div>
    );
  }

  const sortedSubjectGroups = [...skillSubjectGroups].sort((a, b) => {
    const aCount = a.skillGuides.reduce((sum, s) => sum + s.items.length, 0);
    const bCount = b.skillGuides.reduce((sum, s) => sum + s.items.length, 0);
    return bCount - aCount;
  });

  return (
    <div className='flex flex-col gap-x4 border-t border-t-border-subtle p-x2 m:p-x4'>
      {diagnostics.length > 0 && (
        <div className='-mx-x1 flex flex-col gap-x0-half'>
          {diagnostics.map((diagnostic) => (
            <DiagnosticPathItem key={diagnostic.diagnosticId} diagnostic={diagnostic} />
          ))}
        </div>
      )}

      {sortedSubjectGroups.map((group) => (
        <div key={group.subjectId} className='flex flex-col gap-x2'>
          <div className='flex items-center gap-x2'>
            <div className='flex h-x5 w-x5 items-center justify-center rounded-full background-special-subtle'>
              <Icon icon={['fal', 'sparkles']} className='content-special!' />
            </div>
            <div className='flex flex-col'>
              <Text size='l' color='primary'>
                {group.subjectName}
              </Text>
              {group.skillGuides.length === 0 && (
                <Text size='s' color='tertiary'>
                  You can practice all skills within this subject.
                </Text>
              )}
            </div>
          </div>

          <div className='flex flex-col gap-x0-half m:pl-x7'>
            {group.skillGuides.map((guide) => (
              <div key={guide.guideId} className='mb-x1 flex flex-col'>
                {guide.guideName && guide.guideName.toLowerCase() !== 'adaptive' && (
                  <div className='p-x0-half'>
                    <Text
                      as={MarkdownRendererV2}
                      text={guide.guideName}
                      size='s'
                      color='primary'
                      bold
                    />
                  </div>
                )}
                <div className='flex flex-col gap-x0-half'>
                  {guide.items.map((item) => (
                    <SkillPathItem
                      key={item.id}
                      guideLevelId={item.id}
                      name={item.name}
                      code={item.code}
                      dueDate={item.dueDate}
                      myProficiencyLevel={item.myProficiencyLevel}
                    />
                  ))}
                </div>
              </div>
            ))}
            <BrowseAllSkillsLink href={group.browseUrl} />
          </div>
        </div>
      ))}
    </div>
  );
}
